<template>
  <v-container fluid class="mt-16 px-6">
    <v-card flat>
      <!-- Header -->
      <v-card-title class="d-flex align-center flex-wrap">
        <v-icon large color="primary" class="mr-2">mdi-chart-box</v-icon>
        <h2 class="primary--text">Sales Analytics</h2>
        <v-chip small class="ml-3" color="blue-grey" dark v-if="isAdmin">
          Admin — all agents
        </v-chip>
        <v-spacer></v-spacer>
        <v-text-field
          v-model="month"
          type="month"
          label="Month"
          dense
          hide-details
          style="max-width: 170px"
          class="mr-3"
          @change="loadDashboard"
        ></v-text-field>
        <v-btn v-if="isAdmin" color="primary" class="mr-2" @click="openTargets">
          <v-icon left>mdi-target</v-icon>Targets
        </v-btn>
        <v-btn icon x-large @click="refreshAll" :loading="loading">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-card-title>

      <v-tabs v-model="tab" class="px-4">
        <v-tab><v-icon left>mdi-view-dashboard</v-icon>Overview</v-tab>
        <v-tab><v-icon left>mdi-account-star</v-icon>Leads</v-tab>
      </v-tabs>
      <v-divider></v-divider>

      <v-tabs-items v-model="tab">
        <!-- ===================== OVERVIEW ===================== -->
        <v-tab-item>
          <v-card-text>
            <!-- KPI cards -->
            <v-row dense>
              <v-col v-for="k in kpis" :key="k.label" cols="6" sm="4" md="2">
                <v-card class="pa-3 text-center kpi-card" outlined>
                  <v-icon :color="k.color" class="mb-1">{{ k.icon }}</v-icon>
                  <div
                    class="text-h5 font-weight-bold"
                    :class="k.color + '--text'"
                  >
                    {{ k.value }}
                  </div>
                  <div class="text-caption grey--text text-uppercase">
                    {{ k.label }}
                  </div>
                </v-card>
              </v-col>
            </v-row>

            <!-- Target tiers (per agent) — figures up top so the agent sees them -->
            <v-row>
              <v-col
                v-for="c in dashboard"
                :key="'tier-' + c.agent_id"
                cols="12"
                :md="dashboard.length > 1 ? 6 : 12"
              >
                <v-card outlined class="pa-3">
                  <div class="d-flex align-center">
                    <v-icon left color="primary">mdi-account-tie</v-icon>
                    <strong>{{ c.agent_name }}</strong>
                    <v-spacer></v-spacer>
                    <span class="text-caption grey--text"
                      >Pace ₱{{ money(c.pace_per_day) }}/day ·
                      {{ c.remaining_working_days }} days left</span
                    >
                  </div>
                  <div
                    class="
                      d-flex
                      justify-space-between
                      align-center
                      text-caption
                      mt-2
                    "
                  >
                    <span>Closed ₱{{ money(c.closed_amount) }}</span>
                    <v-chip x-small :color="tierChip(c).color" dark>{{
                      tierChip(c).label
                    }}</v-chip>
                  </div>
                  <v-progress-linear
                    :value="tierProgress(c)"
                    height="16"
                    rounded
                    :color="tierBarColor(c)"
                  ></v-progress-linear>
                  <!-- 3 tier figures -->
                  <div class="d-flex mt-2" v-if="tierMilestones(c).length">
                    <div
                      v-for="m in tierMilestones(c)"
                      :key="m.key"
                      class="text-center px-1"
                      style="flex: 1"
                    >
                      <v-icon
                        small
                        :color="m.reached ? 'green' : 'grey lighten-1'"
                        >{{
                          m.reached ? "mdi-check-circle" : "mdi-circle-outline"
                        }}</v-icon
                      >
                      <div
                        class="text-caption font-weight-medium"
                        :class="m.reached ? 'green--text' : 'blue-grey--text'"
                      >
                        {{ m.label }}
                      </div>
                      <div class="text-subtitle-2 font-weight-bold">
                        ₱{{ money(m.amount) }}
                      </div>
                    </div>
                  </div>
                  <div v-else class="text-caption grey--text text-center mt-2">
                    No target set for this month yet.
                  </div>
                  <div
                    class="
                      d-flex
                      justify-space-between
                      text-caption
                      grey--text
                      mt-2
                    "
                  >
                    <span :class="headlineClass(c)"
                      >{{ c.pct_remaining }}% to target</span
                    >
                    <span
                      >{{ c.deals_count }} deals · avg ₱{{
                        money(c.avg_deal)
                      }}</span
                    >
                  </div>
                </v-card>
              </v-col>
            </v-row>

            <!-- Charts row 1 -->
            <v-row class="mt-1">
              <v-col cols="12" md="3">
                <v-card outlined class="pa-2">
                  <div class="chart-title">Closed vs Target</div>
                  <google-chart
                    type="PieChart"
                    :data="closedVsTargetData"
                    :options="donutOptions('', ['#2E7D32', '#E0E0E0'])"
                    :height="240"
                  />
                </v-card>
              </v-col>
              <v-col cols="12" md="3">
                <v-card outlined class="pa-2">
                  <div class="chart-title">Leads by Grade</div>
                  <google-chart
                    type="PieChart"
                    :data="leadsByGradeData"
                    :options="donutOptions('', ['#2E7D32', '#F9A825', '#E53935', '#B0BEC5'])"
                    :height="240"
                  />
                </v-card>
              </v-col>
              <v-col cols="12" md="3">
                <v-card outlined class="pa-2">
                  <div class="chart-title">Leads by Interest</div>
                  <google-chart
                    type="ColumnChart"
                    :data="leadsByCategoryData"
                    :options="columnOptions"
                    :height="240"
                  />
                </v-card>
              </v-col>
              <v-col cols="12" md="3">
                <v-card outlined class="pa-2">
                  <div class="chart-title">Closed ₱ by Agent</div>
                  <google-chart
                    type="BarChart"
                    :data="closedByAgentData"
                    :options="barOptions"
                    :height="240"
                  />
                </v-card>
              </v-col>
            </v-row>

            <!-- Trend -->
            <v-row>
              <v-col cols="12">
                <v-card outlined class="pa-2">
                  <div class="chart-title">
                    Closed vs Target — last 6 months
                  </div>
                  <google-chart
                    type="ComboChart"
                    :data="trendData"
                    :options="trendOptions"
                    :height="300"
                  />
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-tab-item>

        <!-- ===================== LEADS ===================== -->
        <v-tab-item>
          <leads-page :embedded="true" />
        </v-tab-item>
      </v-tabs-items>

      <p class="caption grey--text text-right pr-4 pb-2 mb-0">SalesHub.vue</p>
    </v-card>

    <!-- Manage Targets (admin) -->
    <v-dialog v-model="targetsDialog" width="620" scrollable>
      <v-card>
        <v-card-title
          ><h3>Manage Targets — {{ month }}</h3></v-card-title
        >
        <v-divider></v-divider>
        <v-card-text style="max-height: 60vh">
          <p v-if="targets.length === 0" class="grey--text mt-3">
            No sales agents in this branch.
          </p>
          <div class="caption grey--text mb-2">
            Three tiers per agent — Tier 1 Minimum · Tier 2 Target · Tier 3
            Stretch (should increase left → right).
          </div>
          <v-row
            v-for="t in targets"
            :key="t.agent_id"
            dense
            align="center"
            class="mt-1"
          >
            <v-col cols="12" sm="3" class="font-weight-medium">{{
              t.agent_name
            }}</v-col>
            <v-col cols="4" sm="3">
              <v-text-field
                v-model.number="t.minimum_amount"
                type="number"
                label="① Minimum ₱"
                dense
                hide-details
                clearable
              ></v-text-field>
            </v-col>
            <v-col cols="4" sm="3">
              <v-text-field
                v-model.number="t.target_amount"
                type="number"
                label="② Target ₱"
                dense
                hide-details
              ></v-text-field>
            </v-col>
            <v-col cols="4" sm="3">
              <v-text-field
                v-model.number="t.stretch_amount"
                type="number"
                label="③ Stretch ₱"
                dense
                hide-details
                clearable
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn color="primary" @click="saveTargets" :loading="savingTargets"
            >Save All</v-btn
          >
          <v-btn text @click="targetsDialog = false">Cancel</v-btn>
          <v-spacer></v-spacer>
          <span class="caption grey--text"
            >planned min 250,000 / target 300,000 / stretch 350,000</span
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Login reminder: due follow-ups + behind-target warning (JMB-157) -->
    <v-dialog v-model="loginAlert" width="460">
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon left color="primary">mdi-bell-ring</v-icon>
          <span>Good day{{ userFirstName ? ", " + userFirstName : "" }}!</span>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          <v-alert
            v-if="dueLeads.length"
            type="info"
            text
            dense
            border="left"
            class="mb-3"
          >
            <div class="font-weight-medium">
              {{ dueLeads.length }} follow-up{{
                dueLeads.length > 1 ? "s" : ""
              }}
              due today or overdue
            </div>
            <div class="text-caption">
              Reach out to these leads so none go cold.
            </div>
          </v-alert>

          <!-- The actual due/overdue lead rows (JMB-158) -->
          <v-list v-if="dueLeads.length" dense class="py-0 mb-3 due-leads-list">
            <v-list-item
              v-for="l in dueLeads"
              :key="'due-' + l.id"
              @click="goToLeads"
              class="px-2 due-lead-row"
            >
              <v-list-item-content>
                <v-list-item-title class="font-weight-medium">
                  {{ l.customer_name }}
                </v-list-item-title>
                <v-list-item-subtitle class="text-caption">
                  {{ l.status }}
                  <span v-if="l.contact_number">· {{ l.contact_number }}</span>
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-chip x-small dark :color="overdueColor(l)">
                  {{ l.next_follow_up_date }}
                </v-chip>
              </v-list-item-action>
            </v-list-item>
          </v-list>

          <v-alert
            v-if="targetWarning"
            type="warning"
            text
            dense
            border="left"
            class="mb-0"
          >
            <div class="font-weight-medium">
              Behind target — {{ targetWarning.pct }}% of ₱{{
                money(targetWarning.target)
              }}
            </div>
            <div class="text-caption">
              ₱{{ money(targetWarning.gap) }} to go with only
              {{ targetWarning.daysLeft }} working day{{
                targetWarning.daysLeft === 1 ? "" : "s"
              }}
              left this month.
            </div>
          </v-alert>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn v-if="dueLeads.length" color="primary" text @click="goToLeads">
            <v-icon left>mdi-account-star</v-icon>View Leads
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn text @click="loginAlert = false">Got it</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import GoogleChart from "../components/GoogleChart.vue";
import LeadsPage from "./Leads.vue";

export default {
  components: { GoogleChart, LeadsPage },
  data() {
    return {
      tab: 0,
      month: "",
      loading: false,
      targetsDialog: false,
      savingTargets: false,
      loginAlert: false,
      bluePalette: [
        "#1565C0",
        "#42A5F5",
        "#90CAF9",
        "#0D47A1",
        "#64B5F6",
        "#B0BEC5",
      ],
    };
  },
  computed: {
    ...mapGetters({
      dashboard: "sales/dashboard",
      targets: "sales/targets",
      all_leads: "lead/all_leads",
      user: "auth/user",
      hasPermission: "permission/hasPermission",
      user_permissions: "permission/user_permissions",
    }),
    isAdmin() {
      // "View all agents' leads" is a pure hat-permission gate
      // (leads.view_all, managed on Role Restrictions).
      return this.hasPermission("leads.view_all");
    },
    userFirstName() {
      if (!this.user || !this.user.name) return "";
      return String(this.user.name).trim().split(/\s+/)[0];
    },
    // Leads whose next follow-up is today or in the past and still open.
    // For a level-7 agent, all_leads is already scoped to their own leads.
    dueLeads() {
      const today = new Date().toISOString().slice(0, 10);
      return this.all_leads.filter(
        (l) =>
          l.next_follow_up_date &&
          !["Won", "Lost"].includes(l.status) &&
          l.next_follow_up_date <= today
      );
    },
    // The dashboard card for the logged-in agent (single card in agent scope).
    myCard() {
      if (!this.user || !this.dashboard.length) return null;
      return (
        this.dashboard.find((c) => c.agent_id === this.user.id) ||
        (this.dashboard.length === 1 ? this.dashboard[0] : null)
      );
    },
    // Warn when the agent is well behind target AND the month is nearly over.
    targetWarning() {
      const c = this.myCard;
      if (!c || !c.target_amount) return null;
      const pct = Number(c.pct_achieved) || 0;
      const daysLeft = Number(c.remaining_working_days);
      if (pct < 80 && daysLeft <= 7) {
        return {
          pct: Math.round(pct),
          gap: Math.max(0, c.target_amount - c.closed_amount),
          daysLeft: daysLeft,
          target: c.target_amount,
        };
      }
      return null;
    },
    // ---- Aggregates across the agents in scope ----
    totals() {
      return this.dashboard.reduce(
        (a, c) => {
          a.closed += c.closed_amount;
          a.deals += c.deals_count;
          a.target += c.target_amount;
          a.pipeline += c.pipeline_value;
          a.openLeads += c.open_leads_count;
          return a;
        },
        { closed: 0, deals: 0, target: 0, pipeline: 0, openLeads: 0 }
      );
    },
    kpis() {
      const t = this.totals;
      const avg = t.deals > 0 ? t.closed / t.deals : 0;
      const pctAchieved = t.target > 0 ? (t.closed / t.target) * 100 : 0;
      return [
        {
          label: "Closed Sales",
          value: "₱" + this.compact(t.closed),
          icon: "mdi-cash-multiple",
          color: "green",
        },
        {
          label: "Deals Closed",
          value: t.deals,
          icon: "mdi-handshake",
          color: "blue",
        },
        {
          label: "Avg Deal",
          value: "₱" + this.compact(avg),
          icon: "mdi-chart-line",
          color: "indigo",
        },
        {
          label: "% to Target",
          value: Math.round(pctAchieved) + "%",
          icon: "mdi-target",
          color: pctAchieved >= 100 ? "green" : "orange",
        },
        {
          label: "Open Leads",
          value: t.openLeads,
          icon: "mdi-account-clock",
          color: "teal",
        },
        {
          label: "Pipeline ₱",
          value: "₱" + this.compact(t.pipeline),
          icon: "mdi-filter-variant",
          color: "purple",
        },
      ];
    },
    closedVsTargetData() {
      const t = this.totals;
      const remaining = Math.max(0, t.target - t.closed);
      return [
        ["Metric", "Amount"],
        ["Closed", Math.round(t.closed)],
        ["Remaining", Math.round(remaining)],
      ];
    },
    leadsByGradeData() {
      const order = ["A", "B", "C"];
      const counts = {};
      let ungraded = 0;
      this.all_leads.forEach((l) => {
        if (l.grade) counts[l.grade] = (counts[l.grade] || 0) + 1;
        else ungraded++;
      });
      const rows = [["Grade", "Count"]];
      order.forEach((g) => {
        if (counts[g]) rows.push(["Grade " + g, counts[g]]);
      });
      if (ungraded) rows.push(["Ungraded", ungraded]);
      if (rows.length === 1) rows.push(["No leads", 0]);
      return rows;
    },
    leadsByCategoryData() {
      const counts = {};
      this.all_leads.forEach((l) => {
        const c = l.category || "—";
        counts[c] = (counts[c] || 0) + 1;
      });
      const rows = [["Interest", "Count", { role: "style" }]];
      Object.entries(counts)
        .sort((a, b) => b[1] - a[1])
        .forEach(([name, count], i) => {
          rows.push([name, count, this.bluePalette[i % this.bluePalette.length]]);
        });
      if (rows.length === 1) rows.push(["No leads", 0, "#B0BEC5"]);
      return rows;
    },
    closedByAgentData() {
      const rows = [["Agent", "Closed", { role: "style" }]];
      this.dashboard.forEach((c, i) => {
        rows.push([
          c.agent_name,
          Math.round(c.closed_amount),
          this.bluePalette[i % this.bluePalette.length],
        ]);
      });
      if (rows.length === 1) rows.push(["—", 0, "#B0BEC5"]);
      return rows;
    },
    trendData() {
      // Aggregate each agent's 6-month history by month index.
      const base = this.dashboard[0] && this.dashboard[0].history;
      if (!base)
        return [
          ["Month", "Closed", "Target"],
          ["", 0, 0],
        ];
      const rows = [["Month", "Closed", "Target"]];
      base.forEach((h, idx) => {
        let closed = 0;
        let target = 0;
        this.dashboard.forEach((c) => {
          const e = c.history[idx];
          if (e) {
            closed += e.closed;
            target += e.target;
          }
        });
        rows.push([h.label, Math.round(closed), Math.round(target)]);
      });
      return rows;
    },
    columnOptions() {
      return {
        legend: "none",
        chartArea: { width: "78%", height: "70%" },
        vAxis: { format: "short" },
        backgroundColor: "transparent",
      };
    },
    barOptions() {
      return {
        legend: "none",
        chartArea: { width: "60%", height: "78%" },
        hAxis: { format: "short" },
        backgroundColor: "transparent",
      };
    },
    trendOptions() {
      return {
        seriesType: "area",
        series: { 1: { type: "line", color: "#E53935" } },
        colors: ["#1976D2", "#E53935"],
        legend: { position: "top" },
        chartArea: { width: "88%", height: "72%" },
        vAxis: { format: "short" },
        backgroundColor: "transparent",
      };
    },
  },
  methods: {
    ...mapActions({
      get_dashboard: "sales/get_dashboard",
      get_targets: "sales/get_targets",
      save_target: "sales/save_target",
      get_all_leads: "lead/get_all_leads",
    }),
    money(v) {
      return Number(v || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    compact(v) {
      v = Number(v || 0);
      if (v >= 1e6) return (v / 1e6).toFixed(2) + "M";
      if (v >= 1e3) return Math.round(v / 1e3) + "K";
      return v.toFixed(0);
    },
    donutOptions(title, colors) {
      return {
        pieHole: 0.55,
        legend: { position: "bottom", textStyle: { fontSize: 11 } },
        colors,
        chartArea: { width: "90%", height: "80%" },
        backgroundColor: "transparent",
        title,
      };
    },
    headlineClass(c) {
      if (c.pct_achieved >= 100) return "green--text";
      if (c.pct_achieved >= 50) return "orange--text";
      return "red--text";
    },
    barColor(c) {
      if (c.pct_achieved >= 100) return "green";
      if (c.pct_achieved >= 50) return "amber darken-1";
      return "red";
    },
    // ---- Tiered target (Minimum / Target / Stretch) ----
    tierMilestones(c) {
      const rows = [];
      if (c.minimum_amount) {
        rows.push({
          key: "min",
          label: "① Minimum",
          amount: c.minimum_amount,
          reached: c.closed_amount >= c.minimum_amount,
        });
      }
      if (c.target_amount) {
        rows.push({
          key: "tgt",
          label: "② Target",
          amount: c.target_amount,
          reached: c.closed_amount >= c.target_amount,
        });
      }
      if (c.stretch_amount) {
        rows.push({
          key: "str",
          label: "③ Stretch",
          amount: c.stretch_amount,
          reached: c.closed_amount >= c.stretch_amount,
        });
      }
      return rows;
    },
    tierScaleMax(c) {
      return (
        c.stretch_amount ||
        c.target_amount ||
        c.minimum_amount ||
        c.closed_amount ||
        0
      );
    },
    tierProgress(c) {
      const max = this.tierScaleMax(c);
      return max > 0 ? Math.min((c.closed_amount / max) * 100, 100) : 0;
    },
    tierChip(c) {
      switch (c.tier_reached) {
        case "stretch":
          return { label: "🎉 Stretch reached!", color: "green darken-1" };
        case "target":
          return { label: "✓ Target hit", color: "green" };
        case "minimum":
          return { label: "Minimum met", color: "amber darken-2" };
        default:
          return this.tierMilestones(c).length
            ? { label: "Below minimum", color: "red" }
            : { label: "No target set", color: "grey" };
      }
    },
    tierBarColor(c) {
      if (c.tier_reached === "stretch" || c.tier_reached === "target")
        return "green";
      if (c.tier_reached === "minimum") return "amber darken-1";
      return "blue";
    },
    async loadDashboard() {
      this.loading = true;
      try {
        await this.get_dashboard(this.month);
      } catch (e) {
        this.$alert({
          title: "Error",
          message: e?.response?.data?.message || "Failed to load dashboard.",
          color: "error",
          icon: "mdi-alert-circle",
        });
      } finally {
        this.loading = false;
      }
    },
    async refreshAll() {
      await Promise.all([this.loadDashboard(), this.get_all_leads()]);
    },
    goToLeads() {
      this.tab = 1;
      this.loginAlert = false;
    },
    // Red for overdue, orange for due today (matches Leads.vue).
    overdueColor(l) {
      const today = new Date().toISOString().slice(0, 10);
      return l.next_follow_up_date < today ? "red" : "orange";
    },
    // Pop the reminder once per login session for Sales Agents (the
    // sales.agent_workspace hat) when they have due follow-ups or are behind
    // target near the deadline.
    maybeShowLoginAlert() {
      if (!this.user || !this.hasPermission("sales.agent_workspace")) return;
      if (!this.dueLeads.length && !this.targetWarning) return;
      const key = "sales_login_alert_" + this.user.id;
      try {
        if (sessionStorage.getItem(key)) return;
        sessionStorage.setItem(key, "1");
      } catch (e) {
        // sessionStorage unavailable — still show the alert this time.
      }
      this.loginAlert = true;
    },
    async openTargets() {
      try {
        await this.get_targets(this.month);
        this.targetsDialog = true;
      } catch (e) {
        this.$alert({
          title: "Error",
          message: e?.response?.data?.message || "Failed to load targets.",
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
    },
    async saveTargets() {
      this.savingTargets = true;
      try {
        for (const t of this.targets) {
          await this.save_target({
            agent_id: t.agent_id,
            month: this.month,
            target_amount: t.target_amount || 0,
            minimum_amount:
              t.minimum_amount === "" || t.minimum_amount === null
                ? null
                : t.minimum_amount,
            stretch_amount:
              t.stretch_amount === "" || t.stretch_amount === null
                ? null
                : t.stretch_amount,
          });
        }
        this.targetsDialog = false;
        await this.loadDashboard();
        this.$alert({
          title: "Saved",
          message: "Targets updated.",
          color: "success",
          icon: "mdi-check-circle",
        });
      } catch (e) {
        this.$alert({
          title: "Error",
          message: e?.response?.data?.message || "Failed to save targets.",
          color: "error",
          icon: "mdi-alert-circle",
        });
      } finally {
        this.savingTargets = false;
      }
    },
  },
  watch: {
    // Hat-based users only resolve leads.view_all once permissions finish
    // loading, so re-load the dashboard (admin sees all agents) when it
    // flips true after that.
    isAdmin(v) {
      if (v) this.loadDashboard();
    },
  },
  async created() {
    const now = new Date();
    this.month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
      2,
      "0"
    )}`;
    await this.loadDashboard();
    if (this.all_leads.length === 0) await this.get_all_leads();
    this.maybeShowLoginAlert();
  },
};
</script>

<style scoped>
.kpi-card {
  border-top: 3px solid #1976d2;
}
.chart-title {
  font-size: 0.8rem;
  font-weight: 600;
  color: #546e7a;
  margin-bottom: 4px;
  text-align: center;
}
.due-leads-list {
  max-height: 220px;
  overflow-y: auto;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 4px;
}
.due-lead-row {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}
.due-lead-row:last-child {
  border-bottom: none;
}
</style>
